/** /trip-types — parent-scoped Trip_Type ("Filters") catalog (grid + reorder).
 *
 * List rows only carry `has_image` (not the image chunks), so the Image column
 * shows a small "has image" marker rather than a live thumbnail; the actual
 * image is rendered on the detail page. */
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { Box, Button, CircularProgress } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tourOperatorsApi from '@/api/tourOperators';
import * as tripTypesApi from '@/api/tripTypes';
import type { TripTypeListItem } from '@/api/tripTypes';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { TripTypeCreateDialog } from './TripTypeCreateDialog';

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
];
/** The filters other than the tenant: any of them set hides rows of the one
 *  tenant's sequence, so drag reorder is off (none today; kept for the next one). */
const NON_TENANT_FILTERS = FILTERS.filter((f) => !f.tenantScope);

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const DEFAULT_ORDER = [
  'id',
  'tenant',
  'name',
  'description',
  'has_image',
  'branchio_link',
  'sequence',
];

/** Columns present but hidden until toggled on via the column menu. */
const DEFAULT_HIDDEN = ['branchio_link', 'custom_id', 'updated_ts'];

export function TripTypesListPage() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const canManage = hasPerm('console_trip_types_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'trip_types',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'sequence',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const tenantParam = getParam('tenant');
  // The operators the effective tenant filter names (URL > preset > scope).
  const filterTenants = pageTenantsFrom(tenantParam, useTenantScopeList());
  const tenantId = singleTenantId(filterTenants.map((t) => t.id));

  // The create dialog pre-selects / features the page's effective tenant (D9) —
  // but a trip type belongs to a ROOT operator (its picker is `rootsOnly`), so a
  // licensee the page names (a scope, a hand-edited or cross-page link) is
  // neither. Whether an id has a parent comes from the shared
  // ['tourOperator', id] lookup (a detail page or a single picker may already
  // hold it); until it answers, the id is left out.
  const namedLookups = useQueries({
    queries: filterTenants.map((t) => ({
      queryKey: ['tourOperator', t.id],
      queryFn: () => tourOperatorsApi.retrieve(t.id),
      staleTime: Infinity,
      retry: false,
    })),
  });
  const rootOperators = namedLookups.flatMap((q) => (q.data && q.data.parent_id === null ? [q.data] : []));
  const pageTenants = pageTenantsFrom(tenantParam, rootOperators).filter((t) =>
    rootOperators.some((r) => r.id === t.id),
  );

  // Drag reorder is only coherent when a single tenant's rows are shown in
  // their stored `sequence` order with nothing else filtered out. The tenant
  // filter itself is the precondition, not an obstacle, so it does not count.
  const orderingIsSequence = presets.ordering === 'sequence';
  const otherFilterActive = NON_TENANT_FILTERS.some((f) => getParam(f.param));
  const reorderEnabled = canManage && orderingIsSequence && !otherFilterActive && tenantId !== null;

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: number[]) => tripTypesApi.reorder(tenantId!, orderedIds),
    onSuccess: () => {
      snackbar.show('Order saved.', 'success');
      qc.invalidateQueries({ queryKey: ['trip_types'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Reorder failed.', 'error'),
  });

  const columns = useMemo<GridColDef<TripTypeListItem>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/trip-types/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.description ?? '',
        renderCell: (p) => p.row.description || '—',
      },
      {
        field: 'has_image',
        headerName: 'Image',
        width: 90,
        sortable: false,
        renderCell: (p) =>
          p.row.has_image ? (
            <ImageIcon fontSize="small" color="action" titleAccess="Has image" />
          ) : (
            <ImageNotSupportedIcon fontSize="small" color="disabled" titleAccess="No image" />
          ),
      },
      {
        field: 'branchio_link',
        headerName: 'Branch.io link',
        width: 220,
        sortable: false,
        valueGetter: (_, row) => row.branchio_link ?? '',
        renderCell: (p) => p.row.branchio_link || '—',
      },
      { field: 'sequence', headerName: 'Seq', width: 90 },
      // Already serialized (custom_id is searchable; updated_ts is sortable/whitelisted)
      // but had no column. Default-hidden.
      {
        field: 'custom_id',
        headerName: 'Custom ID',
        width: 140,
        sortable: false,
        valueGetter: (_, row) => row.custom_id ?? '—',
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 150,
        renderCell: (p) => (p.row.updated_ts ? formatRelativeTime(p.row.updated_ts) : '—'),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      tripTypesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'sequence',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: filterTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={filterTenants}
            rootsOnly
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="regions-trip-types"
        title="Trip Types"
        subtitle="Parent-managed trip types — shown as trip filters in the client apps. Drag to reorder within an operator (sorted by sequence)."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New trip type
            </Button>
          )
        }
      />

      <TripTypeCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<TripTypeListItem>
          queryKey={['trip_types']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="trip_types"
          batchActions={[deleteBatchAction('console_trip_types_manage'), batchEditAction('trip_types', 'trip type')]}
          defaultSort={{ field: 'sequence', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          rowReordering={reorderEnabled}
          {...(reorderEnabled
            ? { onRowOrderChange: (ids) => reorderMutation.mutate(ids as number[]) }
            : {})}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or custom ID',
            exportFileName: 'filters',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
